import React, { useState } from 'react';
import './Contact.css';

const industryOptions = [
  'Law Firm',
  'Accounting Firm',
  'School / School Board',
  'Other Enterprise',
  'Other / Not Sure',
];

const sizeOptions = [
  '1–10 employees',
  '11–50 employees',
  '51–200 employees',
  '200+ employees',
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    industry: '', size: '', message: '', urgent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    // ── IMPORTANT ──────────────────────────────────────────────────────────
    // Replace this with your actual form submission logic.
    // Options: Formspree (free), EmailJS, Netlify Forms, your own backend.
    //
    // Example with Formspree:
    //   1. Sign up at formspree.io
    //   2. Create a form and get your endpoint URL
    //   3. Replace the fetch URL below:
    //
    // await fetch('https://formspree.io/f/YOUR_FORM_ID', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(form),
    // });
    // ───────────────────────────────────────────────────────────────────────

    // Simulated delay for demo
    // await new Promise(r => setTimeout(r, 1200));
    // setLoading(false);
    // setSubmitted(true);
    try {
      const res = await fetch('https://formspree.io/f/mrewnyaq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error('Form submission failed');

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Something went wrong sending your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-page">
        <div className="container success-state">
          <div className="success-icon">✓</div>
          <h2>Message received.</h2>
          <p>
            Thanks for reaching out. You'll hear from us within one business day —
            usually much sooner.
          </p>
          <button className="btn-outline" onClick={() => setSubmitted(false)}>
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="page-hero">
        <div className="container">
          <span className="tag">Contact</span>
          <h1>Let's talk about your IT.</h1>
          <p>
            No obligation. Just a straightforward conversation about where your
            organization stands and how we can help.
          </p>
        </div>
      </div>

      <div className="container contact-layout section">
        <div className="contact-info">
          <div className="info-block">
            <span className="tag">Response time</span>
            <p className="info-big">Under 24 hours</p>
            <p className="info-note">Business inquiries typically answered same day.</p>
          </div>

          <div className="info-block">
            <span className="tag">For urgent issues</span>
            <p className="info-big">Mark your message urgent</p>
            <p className="info-note">Use the checkbox on the form — we prioritize these.</p>
          </div>

          <div className="info-block">
            <span className="tag">What to expect</span>
            <ul className="expect-list">
              <li>Brief intro call (15–30 min)</li>
              <li>No-pressure assessment of your current environment</li>
              <li>Clear proposal with scope and pricing</li>
              <li>You decide when you're ready</li>
            </ul>
          </div>

          <div className="info-block">
            <span className="tag">We specialize in</span>
            <div className="info-tags">
              {['Law Firms', 'Accounting Firms', 'School Boards', 'SMB Enterprise'].map(t => (
                <span key={t} className="spec-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Full name *</label>
              <input
                id="name" name="name" type="text" required
                placeholder="Jane Smith"
                value={form.name} onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="company">Company / Organization *</label>
              <input
                id="company" name="company" type="text" required
                placeholder="Smith & Associates LLP"
                value={form.company} onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email address *</label>
              <input
                id="email" name="email" type="email" required
                placeholder="jane@smithlaw.ca"
                value={form.email} onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone number</label>
              <input
                id="phone" name="phone" type="tel"
                placeholder="(416) 000-0000"
                value={form.phone} onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="industry">Industry</label>
              <select id="industry" name="industry" value={form.industry} onChange={handleChange}>
                <option value="">Select industry...</option>
                {industryOptions.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="size">Organization size</label>
              <select id="size" name="size" value={form.size} onChange={handleChange}>
                <option value="">Select size...</option>
                {sizeOptions.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">How can we help? *</label>
            <textarea
              id="message" name="message" required rows={5}
              placeholder="Describe your situation — current IT setup, main pain points, what you're looking for..."
              value={form.message} onChange={handleChange}
            />
          </div>

          <label className="checkbox-label">
            <input
              type="checkbox" name="urgent"
              checked={form.urgent} onChange={handleChange}
            />
            <span>This is urgent — we're dealing with an active issue</span>
          </label>

          <button type="submit" className="btn-primary submit-btn" disabled={loading}>
            {loading ? 'Sending...' : 'Send message →'}
          </button>

          <p className="form-note">
            Your information is never shared. We use it only to respond to your inquiry.
          </p>
        </form>
      </div>
    </div>
  );
}
